import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { ArrowLeft, Printer } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import { formatDate } from '../../utils/dateFormat';

const BatchInvoicePrint = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const ids = searchParams.get('ids')?.split(',').map(Number).filter(Boolean) || [];

  useEffect(() => {
    if (ids.length > 0) fetchInvoices();
    else setLoading(false);
  }, []);

  const fetchInvoices = async () => {
    try {
      const results = await Promise.all(
        ids.map(id => axios.get(`/api/invoices/${id}`).catch(() => null))
      );
      setInvoices(results.filter(r => r !== null).map(r => r.data));
    } catch (error) {}
    finally { setLoading(false); }
  };

  const handlePrint = () => {
    // Open a new window with only the printable content — no layout, no sidebar
    const printWindow = window.open('', '_blank');
    const contentEl = document.getElementById('batch-print-content');
    const clone = contentEl.cloneNode(true);
    
    // Fix relative image URLs to absolute
    clone.querySelectorAll('img').forEach(img => {
      const src = img.getAttribute('src');
      if (src && src.startsWith('/uploads')) {
        img.setAttribute('src', `http://localhost:5000${src}`);
      }
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice Batch Print</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body { font-family: Arial, sans-serif; }
          .print-page { 
            page-break-after: always; 
            page-break-inside: avoid;
            padding: 0;
            margin: 0;
          }
          .print-page:last-child { page-break-after: auto; }
          @media print {
            @page { size: A4; margin: 10mm; }
            .print-page { page-break-after: always !important; page-break-inside: avoid !important; }
            .print-page:last-child { page-break-after: auto !important; }
          }
        </style>
      </head>
      <body>${clone.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const currency = organization?.currency_code;
  const companyName = organization?.companyName || 'AGMS';

  if (loading) return <div className="p-6 text-center">Loading invoices...</div>;
  if (invoices.length === 0) return <div className="p-6 text-center">No invoices selected</div>;

  // Group invoices by customer
  const customerGroups = {};
  invoices.forEach(inv => {
    const custId = inv.customerId || inv.customer?.id || 'unknown';
    const custName = inv.customer?.name || 'Unknown Customer';
    if (!customerGroups[custId]) {
      customerGroups[custId] = {
        customer: inv.customer || { name: custName },
        invoices: []
      };
    }
    customerGroups[custId].invoices.push(inv);
  });

  const groups = Object.values(customerGroups);
  const totalAmount = invoices.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);
  const totalBalance = invoices.reduce((sum, inv) => sum + parseFloat(inv.total || 0) - parseFloat(inv.paidAmount || 0), 0);

  return (
    <div>
      {/* Screen-only controls */}
      <div className="no-print p-4 bg-gray-100 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </button>
        <button onClick={handlePrint} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Printer className="w-4 h-4" /> Print All
        </button>
        <span className="text-sm text-gray-600">
          {groups.length} customer{groups.length > 1 ? 's' : ''} | {invoices.length} invoices | Balance: {formatCurrency(totalBalance, currency)}
        </span>
      </div>

      {/* For each customer: Summary Page + Their Invoice Pages */}
      <div id="batch-print-content">
      {groups.map((group, groupIdx) => {
        const custInvoices = group.invoices;
        const customer = group.customer;
        const custTotal = custInvoices.reduce((s, i) => s + parseFloat(i.total || 0), 0);
        const custPaid = custInvoices.reduce((s, i) => s + parseFloat(i.paidAmount || 0), 0);
        const custBalance = custTotal - custPaid;

        return (
          <React.Fragment key={groupIdx}>
            {/* ===== CUSTOMER SUMMARY PAGE ===== */}
            <div className="print-page" style={{ pageBreakAfter: 'always' }}>
              <div style={{ padding: '40px', fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid #333', paddingBottom: '20px' }}>
                  <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0' }}>{companyName}</h1>
                  {organization?.address && <p style={{ fontSize: '11px', color: '#555', margin: '2px 0' }}>{organization.address}</p>}
                  {organization?.companyContact && <p style={{ fontSize: '11px', color: '#555', margin: '2px 0' }}>Tel: {organization.companyContact}</p>}
                  {organization?.email && <p style={{ fontSize: '11px', color: '#555', margin: '2px 0' }}>{organization.email}</p>}
                  <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '15px', color: '#333' }}>STATEMENT OF ACCOUNT</h2>
                </div>

                {/* Customer Info */}
                <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0' }}>Bill To:</p>
                      <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '3px 0' }}>{customer.name}</p>
                      {customer.contact && <p style={{ fontSize: '11px', margin: '2px 0' }}>📞 {customer.contact}</p>}
                      {customer.email && <p style={{ fontSize: '11px', margin: '2px 0' }}>✉️ {customer.email}</p>}
                      {customer.address && <p style={{ fontSize: '11px', margin: '2px 0' }}>📍 {customer.address}</p>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0' }}>Statement Date:</p>
                      <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '3px 0' }}>{new Date().toLocaleDateString()}</p>
                      <p style={{ fontSize: '11px', margin: '2px 0' }}>Invoices: {custInvoices.length}</p>
                    </div>
                  </div>
                </div>

                {/* Invoice Summary Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '25px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#333', color: '#fff' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Invoice No.</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Vehicle</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Paid</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Balance</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {custInvoices.map((inv, idx) => {
                      const balance = parseFloat(inv.total || 0) - parseFloat(inv.paidAmount || 0);
                      return (
                        <tr key={inv.id} style={{ borderBottom: '1px solid #ddd', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                          <td style={{ padding: '7px 10px' }}>{idx + 1}</td>
                          <td style={{ padding: '7px 10px', fontWeight: '500' }}>{inv.invoiceNumber}</td>
                          <td style={{ padding: '7px 10px' }}>{formatDate(inv.invoiceDate)}</td>
                          <td style={{ padding: '7px 10px' }}>
                            {inv.vehicle ? `${inv.vehicle.make} ${inv.vehicle.model} - ${inv.vehicle.plateNumber || ''}` : '-'}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{formatCurrency(inv.total, currency)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{formatCurrency(inv.paidAmount, currency)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: balance > 0 ? 'bold' : 'normal', color: balance > 0 ? '#c00' : '#060' }}>
                            {formatCurrency(balance, currency)}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                            <span style={{ 
                              padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                              backgroundColor: inv.paymentStatus === 'Paid' ? '#d4edda' : inv.paymentStatus === 'Partial' ? '#fff3cd' : '#f8d7da',
                              color: inv.paymentStatus === 'Paid' ? '#155724' : inv.paymentStatus === 'Partial' ? '#856404' : '#721c24'
                            }}>
                              {inv.paymentStatus}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #333', fontWeight: 'bold', fontSize: '12px' }}>
                      <td colSpan="4" style={{ padding: '10px', textAlign: 'right' }}>TOTALS:</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(custTotal, currency)}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(custPaid, currency)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#c00' }}>{formatCurrency(custBalance, currency)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>

                {/* Summary Box */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
                  <div style={{ width: '280px', border: '1px solid #333', borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 15px', borderBottom: '1px solid #eee' }}>
                      <span style={{ fontSize: '12px' }}>Total Invoiced:</span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{formatCurrency(custTotal, currency)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 15px', borderBottom: '1px solid #eee' }}>
                      <span style={{ fontSize: '12px' }}>Total Paid:</span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#060' }}>{formatCurrency(custPaid, currency)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 15px', backgroundColor: custBalance > 0 ? '#f8d7da' : '#d4edda' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold' }}>Balance Due:</span>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: custBalance > 0 ? '#c00' : '#060' }}>{formatCurrency(custBalance, currency)}</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', fontSize: '10px', color: '#666', textAlign: 'center' }}>
                  <p>This is a computer-generated statement. Please contact us for any discrepancies.</p>
                  <p style={{ marginTop: '5px' }}>{companyName} | {organization?.address || ''} | {organization?.companyContact || ''}</p>
                </div>
              </div>
            </div>

            {/* ===== THIS CUSTOMER'S INVOICE PAGES ===== */}
            {custInvoices.map((invoice, invIdx) => (
              <div key={invoice.id} className="print-page" style={{ pageBreakAfter: 'always' }}>
                <InvoicePrintPage invoice={invoice} organization={organization} currency={currency} />
              </div>
            ))}
          </React.Fragment>
        );
      })}

      {/* Close the printable content wrapper */}
      </div>

      <style>{`
        @media print {
          /* Hide everything except our print content */
          .no-print { display: none !important; }
          
          /* Force the browser to print background colors */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          body, html {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
          
          #root, #root > *, .App, .App > * {
            height: auto !important;
            overflow: visible !important;
          }
          
          /* Hide sidebar and header */
          header, nav, .bg-sidebar-bg, [class*="bg-sidebar"] {
            display: none !important;
          }
          
          /* Remove layout constraints */
          .flex.h-screen, .flex-1, .overflow-hidden, .overflow-auto, .overflow-y-auto {
            display: block !important;
            height: auto !important;
            overflow: visible !important;
            width: 100% !important;
          }
          
          .p-8, .p-6 { padding: 0 !important; }
          
          /* Page break control */
          .print-page {
            page-break-after: always !important;
            page-break-inside: avoid !important;
            break-after: page !important;
            break-inside: avoid !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            max-width: none !important;
            width: 100% !important;
          }
          .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
        @media screen {
          .print-page { 
            max-width: 800px; 
            margin: 20px auto; 
            border: 1px solid #ddd; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            background: white;
          }
        }
      `}</style>
    </div>
  );
};

// Individual invoice print layout
const InvoicePrintPage = ({ invoice, organization, currency }) => {
  const items = invoice.items || [];
  const subtotal = parseFloat(invoice.subtotal || 0);
  const discount = parseFloat(invoice.discount || 0);
  const tax = parseFloat(invoice.tax || 0);
  const total = parseFloat(invoice.total || 0);
  const paidAmount = parseFloat(invoice.paidAmount || 0);
  const balance = total - paidAmount;

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #333', paddingBottom: '15px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>{organization?.companyName || 'AGMS'}</h1>
          {organization?.address && <p style={{ fontSize: '10px', color: '#555', margin: '3px 0' }}>{organization.address}</p>}
          {organization?.companyContact && <p style={{ fontSize: '10px', color: '#555', margin: '3px 0' }}>Tel: {organization.companyContact}</p>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>INVOICE</h2>
          <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '5px 0' }}>{invoice.invoiceNumber}</p>
          <p style={{ fontSize: '10px', margin: '2px 0' }}>Date: {formatDate(invoice.invoiceDate)}</p>
          {invoice.dueDate && <p style={{ fontSize: '10px', margin: '2px 0' }}>Due: {formatDate(invoice.dueDate)}</p>}
        </div>
      </div>

      {/* Customer & Vehicle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>Bill To:</p>
          <p style={{ fontSize: '12px', fontWeight: 'bold', margin: '3px 0' }}>{invoice.customer?.name || '-'}</p>
          {invoice.customer?.contact && <p style={{ fontSize: '10px', margin: '2px 0' }}>{invoice.customer.contact}</p>}
        </div>
        {invoice.vehicle && (
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>Vehicle:</p>
            <p style={{ fontSize: '11px', margin: '3px 0' }}>{invoice.vehicle.make} {invoice.vehicle.model}</p>
            <p style={{ fontSize: '11px', margin: '2px 0' }}>{invoice.vehicle.plateNumber}</p>
          </div>
        )}
      </div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', marginBottom: '15px' }}>
        <thead>
          <tr style={{ backgroundColor: '#333', color: '#fff' }}>
            <th style={{ padding: '6px 8px', textAlign: 'left' }}>#</th>
            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Description</th>
            <th style={{ padding: '6px 8px', textAlign: 'center' }}>Qty</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rate</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '5px 8px' }}>{idx + 1}</td>
              <td style={{ padding: '5px 8px' }}>{item.description || item.name || '-'}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center' }}>{item.quantity || 1}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{formatCurrency(item.unitPrice || item.rate || 0, currency)}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{formatCurrency(item.total || (item.quantity * (item.unitPrice || item.rate || 0)), currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: '220px', fontSize: '11px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #eee' }}>
            <span>Subtotal:</span><span>{formatCurrency(subtotal, currency)}</span>
          </div>
          {discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #eee' }}>
              <span>Discount:</span><span>-{formatCurrency(discount, currency)}</span>
            </div>
          )}
          {tax > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #eee' }}>
              <span>VAT:</span><span>{formatCurrency(tax, currency)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontWeight: 'bold', borderTop: '2px solid #333' }}>
            <span>Total:</span><span>{formatCurrency(total, currency)}</span>
          </div>
          {paidAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#060' }}>
              <span>Paid:</span><span>{formatCurrency(paidAmount, currency)}</span>
            </div>
          )}
          {balance > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontWeight: 'bold', color: '#c00', borderTop: '1px solid #ddd' }}>
              <span>Balance Due:</span><span>{formatCurrency(balance, currency)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchInvoicePrint;
